import React, { useState } from "react";
import {
    Typography,
    Box,
    FormControlLabel,
    Switch,
    Divider,
    Card,
    CardContent,
    Radio,
    RadioGroup,
    FormControl,
    FormLabel,
    Alert,
    Snackbar,
} from "@mui/material";
import classes from "../Settings.module.scss";
import { ComingSoonOverlay } from "../../../components/ui/ComingSoonOverlay/ComingSoonOverlay";

const PrivacyTab: React.FC = () => {
    // Privacy settings state
    const [profileVisibility, setProfileVisibility] = useState("public");
    const [activityVisibility, setActivityVisibility] = useState("connections");
    const [searchableByEmail, setSearchableByEmail] = useState(true);
    const [searchableByPhone, setSearchableByPhone] = useState(false);
    const [showLocationInProfile, setShowLocationInProfile] = useState(true);
    const [showEducationInProfile, setShowEducationInProfile] = useState(true);
    const [showExperienceInProfile, setShowExperienceInProfile] = useState(true);

    // Success notification state
    const [showNotification, setShowNotification] = useState(false);

    // Handle profile visibility change
    const handleProfileVisibilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setProfileVisibility(event.target.value);
        setShowNotification(true);
    };

    // Handle activity visibility change
    const handleActivityVisibilityChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setActivityVisibility(event.target.value);
        setShowNotification(true);
    };

    // Handle toggle changes
    const handleToggleChange =
        (setter: React.Dispatch<React.SetStateAction<boolean>>) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setter(event.target.checked);
            setShowNotification(true);
        };

    // Close notification
    const handleCloseNotification = () => {
        setShowNotification(false);
    };

    return (
        <Box style={{ position: "relative" }}>
            <Box className={classes.contentHeader}>
                <Typography variant="h5" component="h1">
                    Privacy Settings
                </Typography>
                <Typography variant="body2">
                    Control who can see your profile and how your information is used
                </Typography>
            </Box>

            <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
                MAX Aspire is committed to protecting your data privacy. You can review our
                <a
                    href="/privacy-policy"
                    style={{ color: "var(--primary-color)", marginLeft: "4px" }}
                >
                    Privacy Policy
                </a>{" "}
                for more information.
            </Alert>

            {/* Profile Visibility Section */}
            <Box mb={4}>
                <Typography variant="h6" mb={2}>
                    Profile Visibility
                </Typography>
                <Box sx={{ opacity: 0.6, position: "relative" }}>
                    <ComingSoonOverlay
                        message="Advanced visibility controls coming very soon! Help us prioritize features."
                        link="https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog"
                    />
                    <Card
                        sx={{
                            backgroundColor: "var(--hover-background)",
                            boxShadow: "none",
                            border: "1px solid var(--border-color)",
                            transition: "var(--theme-transition)",
                        }}
                    >
                        <CardContent>
                            <FormControl component="fieldset">
                                <FormLabel
                                    component="legend"
                                    sx={{
                                        color: "var(--text-color)",
                                        "&.Mui-focused": { color: "var(--primary-color)" },
                                    }}
                                >
                                    Who can see your profile?
                                </FormLabel>
                                <RadioGroup
                                    aria-label="profile-visibility"
                                    name="profile-visibility"
                                    value={profileVisibility}
                                    onChange={handleProfileVisibilityChange}
                                >
                                    <FormControlLabel
                                        value="public"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Public - Anyone can view your profile"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                    <FormControlLabel
                                        value="connections"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Connections Only - Only your connections can view your profile"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                    <FormControlLabel
                                        value="private"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Private - Only you can view your profile"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Activity Visibility Section */}
            <Box mb={4}>
                <Typography variant="h6" mb={2}>
                    Activity Visibility
                </Typography>
                <Box sx={{ opacity: 0.6, position: "relative" }}>
                    <ComingSoonOverlay
                        message="Advanced controls coming very soon! Help us prioritize features."
                        link="https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog"
                    />
                    <Card
                        sx={{
                            backgroundColor: "var(--hover-background)",
                            boxShadow: "none",
                            border: "1px solid var(--border-color)",
                            transition: "var(--theme-transition)",
                        }}
                    >
                        <CardContent>
                            <FormControl component="fieldset">
                                <FormLabel
                                    component="legend"
                                    sx={{
                                        color: "var(--text-color)",
                                        "&.Mui-focused": { color: "var(--primary-color)" },
                                    }}
                                >
                                    Who can see your activities and posts?
                                </FormLabel>
                                <RadioGroup
                                    aria-label="activity-visibility"
                                    name="activity-visibility"
                                    value={activityVisibility}
                                    onChange={handleActivityVisibilityChange}
                                >
                                    <FormControlLabel
                                        value="public"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Public - Anyone can see your activities"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                    <FormControlLabel
                                        value="connections"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Connections Only - Only your connections can see your activities"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                    <FormControlLabel
                                        value="private"
                                        control={
                                            <Radio
                                                disabled={true}
                                                sx={{
                                                    color: "var(--text-secondary-color)",
                                                    "&.Mui-checked": {
                                                        color: "var(--primary-color)",
                                                    },
                                                }}
                                            />
                                        }
                                        label="Private - Only you can see your activities"
                                        sx={{ color: "var(--text-color)" }}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <Divider sx={{ my: 3, borderColor: "var(--border-color)", opacity: 0.6 }} />

            {/* Search Settings */}
            <Box mb={4}>
                <Typography variant="h6" mb={2}>
                    Search Settings
                </Typography>
                <Box sx={{ opacity: 0.6, position: "relative" }}>
                    <ComingSoonOverlay
                        message="Advanced search settings coming very soon! Help us prioritize features."
                        link="https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog"
                    />
                    <Card
                        sx={{
                            backgroundColor: "var(--hover-background)",
                            boxShadow: "none",
                            border: "1px solid var(--border-color)",
                            transition: "var(--theme-transition)",
                        }}
                    >
                        <CardContent>
                            <Box className={classes.switchContainer}>
                                <Box>
                                    <Typography variant="body1">
                                        Allow others to find you by email
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        People who have your email address can find your profile
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={searchableByEmail}
                                            onChange={handleToggleChange(setSearchableByEmail)}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Box>
                                    <Typography variant="body1">
                                        Allow others to find you by phone number
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        People who have your phone number can find your profile
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={searchableByPhone}
                                            onChange={handleToggleChange(setSearchableByPhone)}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Profile Information Visibility */}
            <Box mb={4}>
                <Typography variant="h6" mb={2}>
                    Profile Information Visibility
                </Typography>
                <Box sx={{ opacity: 0.6, position: "relative" }}>
                    <ComingSoonOverlay
                        message="Coming very soon. Got ideas?"
                        link="https://docs.google.com/forms/d/e/1FAIpQLSeTuQwmG-JMFXfbdgLfxFIp2mKgjQs6RD9-pkjOevZYFN-BCw/viewform?usp=dialog"
                    />
                    <Card
                        sx={{
                            backgroundColor: "var(--hover-background)",
                            boxShadow: "none",
                            border: "1px solid var(--border-color)",
                            transition: "var(--theme-transition)",
                        }}
                    >
                        <CardContent>
                            <Box className={classes.switchContainer}>
                                <Box>
                                    <Typography variant="body1">
                                        Show location on profile
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showLocationInProfile}
                                            onChange={handleToggleChange(setShowLocationInProfile)}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Box>
                                    <Typography variant="body1">
                                        Show education on profile
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showEducationInProfile}
                                            onChange={handleToggleChange(setShowEducationInProfile)}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>

                            <Box className={classes.switchContainer} sx={{ mt: 2 }}>
                                <Box>
                                    <Typography variant="body1">
                                        Show experience on profile
                                    </Typography>
                                </Box>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={showExperienceInProfile}
                                            onChange={handleToggleChange(
                                                setShowExperienceInProfile,
                                            )}
                                            disabled={true}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": {
                                                    color: "var(--primary-color)",
                                                    "& + .MuiSwitch-track": {
                                                        backgroundColor: "var(--primary-light)",
                                                    },
                                                },
                                            }}
                                        />
                                    }
                                    label=""
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <Snackbar
                open={showNotification}
                autoHideDuration={3000}
                onClose={handleCloseNotification}
                message="Privacy settings updated successfully"
                sx={{
                    "& .MuiSnackbarContent-root": {
                        backgroundColor: "var(--primary-color)",
                    },
                }}
            />
        </Box>
    );
};

export default PrivacyTab;
